// stardew-map.ts — Farm map canvas renderer
// Phase 93 (FARM-01): import initHeadCanvas for farmer marker composite.
// The function is async + expensive (Phase 81 full avatar composite); bootstrap
// ONCE into headCanvasCache per payload load. Per RESEARCH Pitfall 5 — do NOT
// call initHeadCanvas inside drawMap (sync tick) or markers will flicker.
import { initHeadCanvas } from './stardew-avatar';
import Panzoom from '@panzoom/panzoom';
// Reads <canvas data-farm-map="..."> JSON, draws (Phase 88) per-farm-type background sprite
// as canvas layer 0, then terrain rectangles (batched via Path2D), then building rectangles
// with text labels on top. Hex `fillRect` is the load-failure / unknown-farm-type fallback.
// Wires a hover tooltip.

// ── Constants ─────────────────────────────────────────────────────────────────

const TILE_PX = 32;             // 2× scale per MAP-03: 1 in-game tile = 32 canvas pixels
// Phase 88: MIN_TILES_X/Y are NOT a universal floor anymore. They are the dimensions floor
// ONLY when FarmTypeMap has no entry for the farm_type (Beach + modded farms). Known farms
// derive canvas size from pixel_w/pixel_h * 2 supplied via payload.background.
const MIN_TILES_X = 80;         // unknown-farm-type bbox floor (matches Standard farm width)
const MIN_TILES_Y = 65;         // unknown-farm-type bbox floor (matches Standard farm height)
const TOOLTIP_OFFSET_X = 12;    // UI-SPEC Interaction contract: 12px right of cursor
const TOOLTIP_OFFSET_Y = 12;    // UI-SPEC Interaction contract: 12px below cursor

// Phase 92 D-03c: sprinkler radius tile-sets per SDV vanilla mechanics.
// Tier 1 = plain (cardinal 4 tiles); tier 2 = surrounding 8 (3×3 minus center);
// tier 3 = 3×3 + outer ring = 24 tiles total. Pressure Nozzle expands one
// ring further (handled in drawHoverOverlay).
const SPRINKLER_TILES: Record<1 | 2 | 3, Array<[number, number]>> = {
    1: [[0, -1], [0, 1], [-1, 0], [1, 0]],
    2: [
        [-1, -1], [0, -1], [1, -1],
        [-1, 0],           [1, 0],
        [-1, 1],  [0, 1],  [1, 1],
    ],
    3: [
        // 3×3 minus center
        [-1, -1], [0, -1], [1, -1],
        [-1, 0],           [1, 0],
        [-1, 1],  [0, 1],  [1, 1],
        // outer ring (16 more tiles forming a 5×5 minus 3×3)
        [-2, -2], [-1, -2], [0, -2], [1, -2], [2, -2],
        [-2, -1],                              [2, -1],
        [-2, 0],                               [2, 0],
        [-2, 1],                               [2, 1],
        [-2, 2],  [-1, 2],  [0, 2],  [1, 2],  [2, 2],
    ],
};

// Canvas terrain palette — UI-SPEC §Canvas terrain palette
// Only 'tilled' ships in MVP; reserved keys included for forward-compat (future fidelity phase).
const TERRAIN_COLORS: Record<string, string> = {
    tilled: '#7a5230',   // HoeDirt brown — MVP: only tile_id shipped
    path:   '#a98765',   // warm path tan — reserved, deferred
    stone:  '#9b9b9b',   // flagstone grey — reserved, deferred
    water:  '#3a7ca5',   // river blue — reserved, deferred
};
const TERRAIN_FALLBACK = '#cccccc';  // neutral grey for unknown tile_id values

// ── Phase 91 (D-01b): Watered HoeDirt visual split ────────────────────────────
// Snap-to value for UI-SPEC Open Item 1: TILLED_DRY_COLOR mirrors the actually-shipped
// Phase 86 baseline hex `#7a5230` (TERRAIN_COLORS.tilled above). TILLED_WATERED_COLOR
// per UI-SPEC D-01b — darker brown for watered tiles.
const TILLED_DRY_COLOR     = '#7a5230';   // matches Phase 86 baseline TERRAIN_COLORS.tilled
const TILLED_WATERED_COLOR = '#5e3e1e';   // UI-SPEC D-01b watered HoeDirt

// Canvas building palette — UI-SPEC §Canvas building palette (per-category)
// Stored as array of {types, color} for linear-scan lookup (hitTestBuilding O(N) acceptable for ~10-30 buildings).
const BUILDING_CATEGORY_COLORS: Array<{ types: string[]; color: string }> = [
    {
        types: ['Farmhouse', 'Log Cabin', 'Plank Cabin', 'Stone Cabin', 'Cabin'],
        color: '#b94a3a',   // rust red — bare 'Cabin' is belt-and-suspenders for BLDSPR-03 (Phase 89)
    },
    {
        types: ['Coop', 'Big Coop', 'Deluxe Coop'],
        color: '#a87547',   // warm clay
    },
    {
        types: ['Barn', 'Big Barn', 'Deluxe Barn'],
        color: '#8b5a2b',   // saddle brown
    },
    {
        types: ['Stable', 'Slime Hutch', 'Fish Pond'],
        color: '#6a4423',   // dark cedar
    },
    {
        types: ['Greenhouse', 'Mill', 'Junimo Hut'],
        color: '#5b9b6a',   // greenhouse green
    },
    {
        types: ['Silo', 'Well', 'Shipping Bin', 'Pet Bowl'],
        color: '#5d6166',   // slate
    },
    {
        types: ['Shed', 'Big Shed'],
        color: '#7e6a4a',   // taupe
    },
    {
        types: ['Earth Obelisk', 'Water Obelisk', 'Desert Obelisk', 'Island Obelisk', 'Gold Clock'],
        color: '#5a4e8a',   // dusk violet
    },
];
const BUILDING_FALLBACK = '#7c7c7c';  // neutral grey for unknown building_type strings

// ── Phase 90 (D-10): Per-category onerror fallback colours ────────────────────
// Mirrors the BUILDING_FALLBACK module-scope const pattern. All five are used
// inside drawMap's per-category branches when sprite.complete===false or
// sprite.naturalWidth===0 (sprite missing or still in flight on first paint).
const FLOORING_FALLBACK   = 'rgba(140,140,140,0.4)';   // translucent grey fill over the tile
const TREE_FALLBACK       = '#3d6b2e';                 // green circle for wild trees
const FRUIT_TREE_FALLBACK = '#2d5a1e';                 // darker-green circle for fruit trees
const BUSH_FALLBACK       = '#3d6b2e';                 // green rounded rect for bush footprint
const FRUIT_DOT_COLOR     = '#cc3333';                 // generic fruit-red dots overlaid on fruit-tree fallback

// ── Interfaces ────────────────────────────────────────────────────────────────

interface TerrainRow { x: number; y: number; id: string; watered: boolean }
interface BuildingRow {
    x: number; y: number; w: number; h: number; type: string;
    // ── Phase 89 additions (UI-SPEC §Payload Shape Extension) ──
    stage: 0 | 1 | 2;
    days_left: number;
    sprite_url: string | null;
    sprite_w: number | null;
    sprite_h: number | null;
    // ── Phase 93 D-03b: inside-animal count badge ──
    animal_badge?: AnimalBadge | null;
    // ── Phase 93 (D-03b / D-08): Per-building indoor animal species histogram ──
    // Keys are animal_type strings (e.g. 'White Chicken'), values are counts.
    // Null/absent = no indoor animals in this building (tooltip unchanged).
    animal_species_histogram?: Record<string, number> | null;
    // ── Phase 93.1 (FARM-01/02/03 / Plan 14): indoor farmer badge ──
    // Indoor farmers (currentLocation = FarmHouse/Cabin/etc.) routed by Plan 13 SQL
    // into a per-building badge instead of an outdoor marker. Null/absent = no
    // indoor farmers (or building has only outdoor farmers, which still appear in
    // payload.farmer_markers — Plan 06 path is untouched per WARNING-1).
    farmer_badge?: FarmerBadge | null;
}

// ── Phase 93.1 (Plan 14 / FARM-01..03): Indoor farmer badge ──────────────────
// Mirrors the AnimalBadge pattern — count + farmer_names for label/+N overflow,
// plus head_appearance_jsons[] (Nette JSON-encoded array of objects per Plan 13)
// used by the SECOND bootstrap loop to pre-load head canvases keyed by name.
type FarmerBadge = {
    count: number;
    farmer_names: string[];
    head_appearance_jsons: Array<Record<string, unknown>>;
}

// ── Phase 93 (ANIM-01..03 / D-05): Animal + farmer marker row types ──────────

type AnimalState = {
    friendship: number;
    happiness: number;
    age_days: number;
    was_pet_today: boolean;
    was_auto_petted: boolean;
    hearts: number;        // pre-computed server-side: floor(friendship / 200), clamped 0..5
    mood: 'happy' | 'content' | 'upset';  // pre-computed server-side from happiness bucket
};

type AnimalRow = {
    x: number;
    y: number;
    animal_name: string;
    animal_type: string;   // 'White Cow', 'Baby Goat', etc.
    sprite_url: string;
    state: AnimalState;
};

type AnimalBadge = {
    emoji: string;   // dominant-species emoji (🐔 / 🐄 / etc.)
    count: number;   // total inside-building animals
};

type FarmerMarker = {
    x: number;
    y: number;
    farmer_name: string;
    head_appearance_json: string;   // JSON for initHeadCanvas (Phase 81 FarmerAppearance shape)
    is_host: boolean;
};

// Phase 88 — payload.background sub-shape. Always present (per StardewModel D-04).
// `string | null` (not optional `?:`) — PHP json_encode emits explicit null, key always present.
interface BackgroundMeta {
    url: string | null;        // CDN PNG URL; null for unknown / Beach / modded farms
    pixel_w: number | null;    // sprite intrinsic width in pixels; null if no sprite
    pixel_h: number | null;    // sprite intrinsic height in pixels; null if no sprite
    fallback_hex: string;      // ALWAYS present — even for unknown types (FALLBACK)
}

// ── Phase 90 (D-12 / B1): payload row types ───────────────────────────────────
// Mirror the StardewModel::getFarmMapPayload @return docblock shape verbatim.
// All 5 categories ship sprite_url as a (server-resolved) string per D-03/D-13.

type FlooringRow = {
    x: number;
    y: number;
    sprite_url: string;
};

type TreeRow = {
    x: number;
    y: number;
    species: string;
    stage: number;     // 0..4 normal, 5 = stump
    sprite_url: string;
    display_name: string;
};

type FruitTreeRow = {
    x: number;
    y: number;
    species: string;
    stage: number;          // 0..4 (mature == 4)
    fruit_count: number;    // 0..3 — clamped server-side
    sprite_url: string;
    display_name: string;
};

type BushRow = {
    x: number;
    y: number;
    type: string;
    w: number;              // footprint width in tiles (1, 2, or 3)
    h: number;              // footprint height in tiles (1 or 3)
    sprite_url: string;
    display_name: string;
};

// Phase 90 B1: discriminated union for the AUTHORITATIVE cross-category merged
// stream. Plan 07 (StardewModel) builds payload.tall_features by walking each
// per-category array, tagging entries with a `category` discriminator, and
// running ONE usort by tile_y ASC across the merged list. The renderer iterates
// in payload order (ZERO sort, ZERO merge per repaint) and dispatches by
// `category` — true cross-category vertical occlusion per D-09.
type TallFeatureRow =
    | (TreeRow      & { category: 'tree' })
    | (FruitTreeRow & { category: 'fruit_tree' })
    | (BushRow      & { category: 'bush' });

// ── Phase 91 (CROP-02 / UI-SPEC §Payload Shape Extension): Crop row type ──────
// Server-shaped per Plan 05 (StardewModel). `sprite_url` may be null when the
// CropSpriteMap has no entry for harvest_id (server-side miss → renderer falls
// back to D-10 shapes). 0-indexed `phase`; tooltip renders `phase + 1` per
// UI-SPEC Copywriting Contract.
type CropRow = {
    x: number;
    y: number;
    harvest_id: string;
    phase: number;
    phase_count: number;
    dead: boolean;
    sprite_url: string | null;
    display_name: string;
};

// ── Phase 92 (OBJ-01..04 / D-08): Objects layer types ────────────────────────
// Shape mirrors CONTEXT D-08 backend payload exactly. `state` is a category-
// branched bag of optional fields — server-side guarantees presence of the
// relevant keys per category but renderer treats them all as optional for
// defensive null-coalesce per UI-SPEC error-state contract.
type ObjectState = {
    // chest:
    color_packed?: number;
    item_count?: number;
    // sprinkler:
    tier?: 1 | 2 | 3;
    has_pressure_nozzle?: boolean;
    has_enricher?: boolean;
    // scarecrow:
    radius?: number;
    // machine:
    held_item_name?: string | null;
    held_item_quality?: number | null;
    minutes_until_ready?: number;
    ready?: boolean;  // pre-computed server-side per CONTEXT D-08
    // crabpot:
    bait_name?: string | null;
};

type ObjectRow = {
    x: number;
    y: number;
    category: 'chest' | 'sprinkler' | 'scarecrow' | 'crabpot' | 'machine' | 'other';
    parent_sheet_index: number;
    is_big_craftable: boolean;
    name: string;
    display_name: string;
    sprite_url: string | null;
    state: ObjectState;
};

interface MapPayload {
    terrain: TerrainRow[];
    buildings: BuildingRow[];
    background: BackgroundMeta;
    // ── Phase 90 (TERR-01..05 / D-12 + B1) ──
    flooring: FlooringRow[];
    trees: TreeRow[];
    fruit_trees: FruitTreeRow[];
    bushes: BushRow[];
    tall_features: TallFeatureRow[];
    // ── Phase 91 (CROP-02) ──
    crops: CropRow[];
    // ── Phase 92 (OBJ-01..04) ──
    objects: ObjectRow[];
    // ── Phase 93 (ANIM-01..03 / FARM-01..02 / D-05) ──
    // animals: on-Farm animals only (server filters currentLocation=Farm).
    // farmer_markers: on-Farm farmers only (server filters currentLocation=Farm per D-04d).
    animals?: AnimalRow[];
    farmer_markers?: FarmerMarker[];
}

// Phase 88 — module-scope image cache. Mirrors BUILDING_CATEGORY_COLORS module-scope pattern.
// Key: CDN URL. Value: HTMLImageElement (may be in-flight; readiness checked via
// `img.complete && img.naturalWidth > 0`). Insert at fetch initiation (NOT at onload)
// to dedupe rapid double-fetch (research Pitfall 2).
const imgCache = new Map<string, HTMLImageElement>();

// Phase 89 — module-scope dedupe set for per-URL sprite-load failure warnings (D-07, RESEARCH Pitfall 9).
// MUST be module-scope, NOT inside drawMap — drawMap repaints on every onload, which would reset
// a function-local Set every time and re-warn on every repaint.
const warnedSet = new Set<string>();

// Phase 92 D-03a: chest tint cache. Key = `${sprite.src}|${packedColor}`.
// Per-(sprite,color) memoization avoids recomposite on every drawMap repaint.
// 92-REVIEW WR-05: bounded LRU (128 entries). Map iteration order is insertion
// order in ES2015+, so deleting+re-inserting a key on read moves it to the
// "most recently used" tail. When size exceeds the cap, evict the head
// (least-recently-used). 128 entries × ~16x16 RGBA canvases ≈ trivial memory;
// the cap exists to guard against pathological saves with thousands of unique
// chest colors that would otherwise leak canvas memory indefinitely.
const TINTED_SPRITE_CACHE_CAP = 128;
const tintedSpriteCache = new Map<string, HTMLCanvasElement>();

// Phase 93 (FARM-01 / RESEARCH Pitfall 5): per-farmer head canvas cache.
// Key: farmer_name. Value: offscreen HTMLCanvasElement (32×40 head crop via initHeadCanvas).
// Populated ONCE per payload load via bootstrapHeadCanvases() before the first drawMap call;
// subsequent drawMap calls are sync drawImage from cache — no flicker (RESEARCH Pitfall 5).
const headCanvasCache = new Map<string, HTMLCanvasElement>();

// Phase 89 — module-init scaffold-hatch pattern (UI-SPEC §Construction-Stage Visual Contract).
// 8×8 repeating tile with two warm-wood diagonals at 30% alpha on transparent backdrop. Built once
// via an IIFE at module load — no per-frame regeneration cost. Returns the CanvasPattern (or null
// if the document API is missing, e.g. SSR — caller guards before fillStyle assignment).
const SCAFFOLD_HATCH: CanvasPattern | null = (() => {
    if (typeof document === 'undefined') return null;
    const hatchCanvas = document.createElement('canvas');
    hatchCanvas.width  = 8;
    hatchCanvas.height = 8;
    const hctx = hatchCanvas.getContext('2d');
    if (!hctx) return null;
    hctx.clearRect(0, 0, 8, 8);
    hctx.strokeStyle = '#8b6a3a';  // warm wood-brown — UI-SPEC token
    hctx.globalAlpha = 0.30;
    hctx.lineWidth   = 1;
    // Two anti-diagonals across the 8×8 cell so the repeat reads as a continuous diagonal lattice.
    hctx.beginPath();
    hctx.moveTo(0, 8); hctx.lineTo(8, 0);
    hctx.moveTo(-1, 1); hctx.lineTo(1, -1);  // wrap-edge stitch top-left
    hctx.moveTo(7, 9); hctx.lineTo(9, 7);    // wrap-edge stitch bottom-right
    hctx.stroke();
    hctx.globalAlpha = 1.0;
    // createPattern returns CanvasPattern | null in TS lib; we have a valid source canvas.
    const probeCtx = document.createElement('canvas').getContext('2d');
    return probeCtx ? probeCtx.createPattern(hatchCanvas, 'repeat') : null;
})();

// ── Helpers ───────────────────────────────────────────────────────────────────

function getBuildingColor(type: string): string {
    for (const cat of BUILDING_CATEGORY_COLORS) {
        if (cat.types.includes(type)) return cat.color;
    }
    return BUILDING_FALLBACK;
}

// ── Phase 90 tall-feature draw routines (D-09 dispatch helpers) ───────────────
// Each routine draws ONE entry from payload.tall_features. Bottom-aligned
// anchoring per Phase 89 D-05: dy = (item.y + h) * TILE_PX - sprite_h * 2 where
// sprite_h is the intrinsic image height (renderer scales 2× per MAP-03). When
// the sprite isn't yet loaded / failed to load, fall back to the per-category
// shape per D-10.

function drawTreeFeature(ctx: CanvasRenderingContext2D, t: TreeRow): void {
    const sprite = t.sprite_url ? imgCache.get(t.sprite_url) : undefined;
    if (sprite && sprite.complete && sprite.naturalWidth > 0) {
        const sW = sprite.naturalWidth;
        const sH = sprite.naturalHeight;
        const dw = sW * 2;
        const dh = sH * 2;
        // h = 1 for trees per D-09 footprint contract
        const dx = t.x * TILE_PX - (dw - TILE_PX) / 2;     // centre horizontally over 1-tile footprint
        const dy = (t.y + 1) * TILE_PX - dh;               // bottom-aligned to the tile's bottom edge
        ctx.drawImage(sprite, dx, dy, dw, dh);
        return;
    }
    // D-10 fallback: stumps → small brown rect; otherwise green circle scaled by stage.
    const cx = t.x * TILE_PX + TILE_PX / 2;
    const cy = t.y * TILE_PX + TILE_PX / 2;
    if (t.stage === 5) {
        // Stump — small brown rect centred on the tile.
        ctx.fillStyle = '#6a4423';
        const sw = TILE_PX * 0.5;
        const sh = TILE_PX * 0.4;
        ctx.fillRect(cx - sw / 2, cy - sh / 2, sw, sh);
    } else {
        const radius = TILE_PX * (0.3 + t.stage * 0.1);
        ctx.fillStyle = TREE_FALLBACK;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
    }
}

function drawFruitTreeFeature(ctx: CanvasRenderingContext2D, f: FruitTreeRow): void {
    const sprite = f.sprite_url ? imgCache.get(f.sprite_url) : undefined;
    if (sprite && sprite.complete && sprite.naturalWidth > 0) {
        const sW = sprite.naturalWidth;
        const sH = sprite.naturalHeight;
        const dw = sW * 2;
        const dh = sH * 2;
        const dx = f.x * TILE_PX - (dw - TILE_PX) / 2;
        const dy = (f.y + 1) * TILE_PX - dh;
        ctx.drawImage(sprite, dx, dy, dw, dh);
        return;
    }
    // D-10 fallback: darker-green circle + N small fruit-coloured dots.
    const cx = f.x * TILE_PX + TILE_PX / 2;
    const cy = f.y * TILE_PX + TILE_PX / 2;
    const radius = TILE_PX * (0.3 + Math.min(4, f.stage) * 0.1);
    ctx.fillStyle = FRUIT_TREE_FALLBACK;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    // Overlay fruit_count small dots arranged along the top of the canopy.
    const fruits = Math.max(0, Math.min(3, f.fruit_count));
    if (fruits > 0) {
        ctx.fillStyle = FRUIT_DOT_COLOR;
        const dotR = Math.max(2, TILE_PX * 0.08);
        const spread = radius * 0.7;
        for (let i = 0; i < fruits; i++) {
            const offset = fruits === 1 ? 0 : (i - (fruits - 1) / 2) * (spread / fruits);
            const fx = cx + offset;
            const fy = cy - radius * 0.4;
            ctx.beginPath();
            ctx.arc(fx, fy, dotR, 0, Math.PI * 2);
            ctx.fill();
        }
    }
}

function drawBushFeature(ctx: CanvasRenderingContext2D, b: BushRow): void {
    const sprite = b.sprite_url ? imgCache.get(b.sprite_url) : undefined;
    if (sprite && sprite.complete && sprite.naturalWidth > 0) {
        const sW = sprite.naturalWidth;
        const sH = sprite.naturalHeight;
        const dw = sW * 2;
        const dh = sH * 2;
        // Bush footprint w×h tiles; centre over the footprint, bottom-aligned to its south edge.
        const dx = b.x * TILE_PX + (b.w * TILE_PX - dw) / 2;
        const dy = (b.y + b.h) * TILE_PX - dh;
        ctx.drawImage(sprite, dx, dy, dw, dh);
        return;
    }
    // D-10 fallback: green rounded rect filling the (b.w × b.h) footprint.
    const fx = b.x * TILE_PX + 2;
    const fy = b.y * TILE_PX + 2;
    const fw = b.w * TILE_PX - 4;
    const fh = b.h * TILE_PX - 4;
    ctx.fillStyle = BUSH_FALLBACK;
    const r = 4;
    ctx.beginPath();
    // Hand-rolled rounded rect (avoids ctx.roundRect — not in es2017 lib for older TS runtimes).
    ctx.moveTo(fx + r, fy);
    ctx.lineTo(fx + fw - r, fy);
    ctx.quadraticCurveTo(fx + fw, fy, fx + fw, fy + r);
    ctx.lineTo(fx + fw, fy + fh - r);
    ctx.quadraticCurveTo(fx + fw, fy + fh, fx + fw - r, fy + fh);
    ctx.lineTo(fx + r, fy + fh);
    ctx.quadraticCurveTo(fx, fy + fh, fx, fy + fh - r);
    ctx.lineTo(fx, fy + r);
    ctx.quadraticCurveTo(fx, fy, fx + r, fy);
    ctx.closePath();
    ctx.fill();
}

// ── Phase 91 (CROP-02 / D-09 / D-10): Crop draw routine ───────────────────────
// Sprite-or-fallback per Pattern E. Bottom-anchored 1×1-tile-wide × 2-tile-tall
// at 2× scale (native 16×32 → 32×64 canvas px). dy = (c.y + 1) * TILE_PX - 64
// so the crop's base aligns to the bottom of its tile and the top half
// (foliage) overhangs the tile to the north — matches in-game render.
// D-10 fallback: alive → green circle scaled by growth phase; dead → brown
// rounded-edged rect in the lower half of the 2-tile slot.
function drawCropFeature(ctx: CanvasRenderingContext2D, c: CropRow): void {
    const sprite = c.sprite_url ? imgCache.get(c.sprite_url) : undefined;
    if (sprite && sprite.complete && sprite.naturalWidth > 0) {
        // 5-arg drawImage per Phase 89 D-03 / 91-PATTERNS.md Pattern C — no source-rect math.
        // Bottom-anchored: dy = (c.y + 1) * TILE_PX - 64 so 2-tile-tall sprite hangs upward.
        ctx.drawImage(sprite, c.x * TILE_PX, (c.y + 1) * TILE_PX - 64, 32, 64);
        return;
    }
    // D-10 fallback paths.
    const cx = c.x * TILE_PX + TILE_PX / 2;
    const cy = c.y * TILE_PX + TILE_PX / 2;
    if (c.dead) {
        // Brown rect — lower-half of the 2-tile-tall crop slot.
        ctx.fillStyle = '#6b4423';
        const sw = TILE_PX * 0.5;
        const sh = TILE_PX * 0.4;
        ctx.fillRect(cx - sw / 2, cy - sh / 2, sw, sh);
    } else {
        // Alive — green circle scaled by growth phase.
        const radius = TILE_PX * (0.2 + c.phase * 0.08);
        ctx.fillStyle = '#4a7c2a';
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
    }
}

// ── Phase 93 (D-03c): Rounded-rect helper for animal_badge pill ──────────────
// Hand-rolled because ctx.roundRect is ES2022+ and not available in all targets.
// Mirrors the drawBushFeature quadraticCurveTo pattern above but uses arcTo for
// tighter corner control per RESEARCH §Per-building animal_badge.
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y,     x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x,     y + h, r);
    ctx.arcTo(x,     y + h, x,     y,     r);
    ctx.arcTo(x,     y,     x + w, y,     r);
    ctx.closePath();
}

// ── Phase 93 (D-07): Animal silhouette fallback ───────────────────────────────
// Solid-grey species-shaped shape via substring match on animal_type.
// Drawn when sprite is missing/failed (D-07 per-category fallback contract).
function drawAnimalFallback(ctx: CanvasRenderingContext2D, x: number, y: number, animalType: string): void {
    const cx = x * TILE_PX + TILE_PX / 2;
    const cy = y * TILE_PX + TILE_PX / 2;
    ctx.fillStyle = '#888888';
    if (animalType.includes('Cow') || animalType.includes('Goat') || animalType.includes('Pig')) {
        // Bulkier: wide ellipse for body + small circle for head
        ctx.beginPath();
        ctx.ellipse(cx, cy, TILE_PX * 0.4, TILE_PX * 0.28, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx + TILE_PX * 0.35, cy - TILE_PX * 0.15, TILE_PX * 0.15, 0, Math.PI * 2);
        ctx.fill();
    } else if (animalType.includes('Chicken') || animalType.includes('Duck')
               || animalType.includes('Dinosaur') || animalType.includes('Rabbit')
               || animalType.includes('Ostrich')) {
        // Tall narrow: small circle (head) above a rectangle (body)
        ctx.fillRect(cx - TILE_PX * 0.15, cy - TILE_PX * 0.1, TILE_PX * 0.3, TILE_PX * 0.35);
        ctx.beginPath();
        ctx.arc(cx, cy - TILE_PX * 0.2, TILE_PX * 0.15, 0, Math.PI * 2);
        ctx.fill();
    } else if (animalType.includes('Sheep')) {
        // Fluffy: large circle body
        ctx.beginPath();
        ctx.arc(cx, cy, TILE_PX * 0.32, 0, Math.PI * 2);
        ctx.fill();
    } else {
        // Generic: plain circle
        ctx.beginPath();
        ctx.arc(cx, cy, TILE_PX * 0.28, 0, Math.PI * 2);
        ctx.fill();
    }
}

// ── Phase 93 (ANIM-01..02 / D-06 layer 7): Animal draw routine ────────────────
// Sprite-or-fallback per RESEARCH §Renderer animals branch (lines 664-678).
// Bottom-aligned anchoring per Phase 89 D-05: dy = (y + 1) * TILE_PX - h * 2.
// Reuses module-scope imgCache + warnedSet VERBATIM (RESEARCH Pitfall 4 / PATTERNS
// §Module-scope imgCache + warnedSet reuse). Sprite loading deferred to init()
// sprite-fetch loop (same pattern as Phase 90/91/92 — no per-drawMap load calls).
function drawAnimalFeature(ctx: CanvasRenderingContext2D, a: AnimalRow): void {
    const sprite = a.sprite_url ? imgCache.get(a.sprite_url) : undefined;
    if (sprite && sprite.complete && sprite.naturalWidth > 0) {
        const w = sprite.naturalWidth * 2;    // 2× CSS scale per Phase 86 MAP-03 baseline
        const h = sprite.naturalHeight * 2;
        const dx = a.x * TILE_PX;
        const dy = (a.y + 1) * TILE_PX - h;  // bottom-anchor (Phase 89 D-05 formula)
        ctx.drawImage(sprite, dx, dy, w, h);
        return;
    }
    // D-07 fallback — species-shaped silhouette
    drawAnimalFallback(ctx, a.x, a.y, a.animal_type);
}

// ── Phase 92 (D-03a): Chest tint via offscreen canvas ────────────────────────
// XNA Color.PackedValue is ABGR little-endian per RESEARCH §Pattern 2.
// VERIFICATION GATE (Task 4): one non-default-color chest visual check —
// sentinel 0xFF000000 (R=B=G=0, A=255) decodes identically under both ARGB
// and ABGR, so doesn't catch the bug. If chests tint with R/B swapped,
// flip to ARGB: `r = (packed>>>16) & 0xFF; b = packed & 0xFF`.
// Cached per (sprite.src, packedColor) — recomposite happens once per unique
// chest color, not per drawMap repaint.
// 92-REVIEW WR-05: return type widened to CanvasImageSource so the
// graceful-fallback path can hand back the original HTMLImageElement when
// getContext('2d') returns null (Firefox with prefers-reduced-data,
// headless DOMs, etc). The drawObjectFeature caller passes the result
// directly into ctx.drawImage which accepts CanvasImageSource — no
// type pivot needed there.
function getTintedChest(base: HTMLImageElement, packed: number): CanvasImageSource {
    const cacheKey = `${base.src}|${packed}`;
    const cached = tintedSpriteCache.get(cacheKey);
    if (cached) {
        // LRU touch: move to MRU tail by re-inserting.
        tintedSpriteCache.delete(cacheKey);
        tintedSpriteCache.set(cacheKey, cached);
        return cached;
    }

    const w = base.width, h = base.height;
    const off = document.createElement('canvas');
    off.width = w; off.height = h;
    const octx = off.getContext('2d');
    if (!octx) {
        // 92-REVIEW WR-05: graceful fallback. getContext can return null in
        // restricted browser modes / headless envs. Returning the untinted
        // base is strictly better than throwing — chest tint is decorative.
        return base;
    }
    octx.imageSmoothingEnabled = false;
    octx.drawImage(base, 0, 0);
    octx.globalCompositeOperation = 'multiply';
    // ABGR unpack per RESEARCH §Pattern 2
    const a = (packed >>> 24) & 0xFF;
    const b = (packed >>> 16) & 0xFF;
    const g = (packed >>> 8)  & 0xFF;
    const r =  packed         & 0xFF;
    octx.fillStyle = `rgba(${r},${g},${b},${a / 255})`;
    octx.fillRect(0, 0, w, h);
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(base, 0, 0);

    // 92-REVIEW WR-05: bounded LRU. Evict the least-recently-used entry
    // (Map keys iterate in insertion order; the first key is the oldest).
    if (tintedSpriteCache.size >= TINTED_SPRITE_CACHE_CAP) {
        const oldest = tintedSpriteCache.keys().next().value;
        if (oldest !== undefined) {
            tintedSpriteCache.delete(oldest);
        }
    }
    tintedSpriteCache.set(cacheKey, off);
    return off;
}

// ── Phase 92 (OBJ-01..04 / D-09 / D-10): Object draw routine ─────────────────
// Sprite-or-fallback per Pattern E. Anchor branches by is_big_craftable per
// Pitfall 8 — small objects (16×16) sit IN their tile; big craftables (16×32)
// bottom-anchor with upward overhang.
// Chest tint: D-03a sentinel-skip when color_packed === 0xFF000000.
// Ready badge: D-03b 4px green dot top-right when state.ready === true.
function drawObjectFeature(ctx: CanvasRenderingContext2D, o: ObjectRow): void {
    const baseSprite = o.sprite_url ? imgCache.get(o.sprite_url) : undefined;
    let sprite: CanvasImageSource | undefined =
        baseSprite && baseSprite.complete && baseSprite.naturalWidth > 0
            ? baseSprite
            : undefined;

    // D-03a chest tint (non-sentinel colors only).
    // 92-REVIEW WR-03: use explicit existence check, not truthiness — a
    // legit player-chosen black (color_packed = 0) is a valid tint and
    // must NOT be skipped just because 0 is falsy in JS.
    if (sprite && o.category === 'chest'
        && typeof o.state?.color_packed === 'number'
        && o.state.color_packed !== 0xFF000000) {
        sprite = getTintedChest(baseSprite!, o.state.color_packed);
    }

    if (sprite) {
        if (o.is_big_craftable) {
            // 16×32 native; draw at 32×64 (2× canvas scale); bottom-anchor
            ctx.drawImage(sprite, o.x * TILE_PX, (o.y + 1) * TILE_PX - 64, 32, 64);
        } else {
            // 16×16 native; draw at TILE_PX × TILE_PX; in-tile anchor (Pitfall 8 — crab pots)
            ctx.drawImage(sprite, o.x * TILE_PX, o.y * TILE_PX, TILE_PX, TILE_PX);
        }

        // D-03b ready badge
        if (o.state?.ready === true) {
            const bx = (o.x + 1) * TILE_PX - 6;
            const by = o.y * TILE_PX + 2;
            ctx.fillStyle = '#3fbf3f';
            ctx.beginPath();
            ctx.arc(bx, by, 3, 0, Math.PI * 2);
            ctx.fill();
        }
        return;
    }

    // D-10 per-category fallback
    const cx = o.x * TILE_PX + TILE_PX / 2;
    const cy = o.y * TILE_PX + TILE_PX / 2;
    switch (o.category) {
        case 'chest':
            ctx.fillStyle = '#8b5a2b';
            ctx.fillRect(cx - TILE_PX * 0.35, cy - TILE_PX * 0.35, TILE_PX * 0.7, TILE_PX * 0.7);
            break;
        case 'sprinkler':
            ctx.fillStyle = '#2a9d9d';
            ctx.beginPath(); ctx.arc(cx, cy, TILE_PX * 0.3, 0, Math.PI * 2); ctx.fill();
            break;
        case 'scarecrow':
            ctx.fillStyle = '#6a4423';
            ctx.fillRect(cx - TILE_PX * 0.1, cy - TILE_PX * 0.75, TILE_PX * 0.2, TILE_PX * 1.5);
            break;
        case 'crabpot':
            ctx.fillStyle = '#1f3f7a';
            ctx.beginPath(); ctx.arc(cx, cy, TILE_PX * 0.3, 0, Math.PI * 2); ctx.fill();
            break;
        case 'machine':
            ctx.fillStyle = '#888';
            ctx.fillRect(cx - TILE_PX / 2, (o.y - 1) * TILE_PX, TILE_PX, TILE_PX * 2);
            break;
        default:
            ctx.fillStyle = '#a05ac8';
            ctx.beginPath(); ctx.arc(cx, cy, TILE_PX * 0.3, 0, Math.PI * 2); ctx.fill();
            break;
    }
}

// ── Phase 93 (FARM-01 / RESEARCH §Pitfall 5 + §Pattern 4): Head canvas bootstrap ──
// Bootstraps headCanvasCache ONCE before the first drawMap call. Uses Promise.all so
// all farmers initialise concurrently. Keyed by farmer_name (unique per farm per D-04e).
// If JSON.parse throws (T-93-06-02 mitigation — malformed head_appearance_json): skip
// silently so one bad farmer doesn't prevent all others from rendering.
async function bootstrapHeadCanvases(payload: MapPayload): Promise<void> {
    await Promise.all((payload.farmer_markers ?? []).map(async (m) => {
        const off = document.createElement('canvas');
        off.width  = 32;
        off.height = 40;
        try {
            // T-93-06-02: JSON.parse inside try/catch — malformed JSON → skip marker silently
            const appearance = JSON.parse(m.head_appearance_json);
            await initHeadCanvas(off, appearance);
            headCanvasCache.set(m.farmer_name, off);
        } catch {
            // Malformed head_appearance_json: skip this farmer's marker silently.
            // The drawMap farmer loop guards via `if (!off) continue` so no crash.
        }
    }));

    // ── Phase 93.1 (Plan 14 / WARNING-1): SECOND bootstrap loop for indoor farmer badges ──
    // Plan 06 marker loop above preserved verbatim per WARNING-1 (true extension, not rewrite).
    // Iterate every building's farmer_badge.farmer_names and initialise a 32×40 head canvas
    // alongside the per-name appearance object. Dedup guard: skip if name already in cache
    // (defensive — post-Plan-13 SQL split a farmer is only in ONE of marker/badge but the
    // guard ensures bit-for-bit idempotency if the split ever leaks).
    const badgePairs: Array<{ name: string; appearance: Record<string, unknown> | string }> = [];
    for (const b of payload.buildings ?? []) {
        const fb = b.farmer_badge;
        if (!fb || fb.count <= 0) continue;
        const n = Math.min(fb.farmer_names.length, fb.head_appearance_jsons.length);
        for (let i = 0; i < n; i++) {
            badgePairs.push({ name: fb.farmer_names[i], appearance: fb.head_appearance_jsons[i] as Record<string, unknown> | string });
        }
    }
    await Promise.all(badgePairs.map(async (pair) => {
        if (headCanvasCache.has(pair.name)) return;   // dedup with Plan 06 marker loop
        const off = document.createElement('canvas');
        off.width  = 32;
        off.height = 40;
        try {
            // Defensive: if Nette double-encoded the appearance into a string, JSON.parse;
            // otherwise the array element already arrived as an object (Plan 13 default).
            const appearance = typeof pair.appearance === 'string'
                ? JSON.parse(pair.appearance)
                : pair.appearance;
            await initHeadCanvas(off, appearance);
            headCanvasCache.set(pair.name, off);
        } catch {
            // Malformed badge appearance: skip silently — Task 2 render guards via
            // `if (!off) continue` (race-safe pattern) so one bad farmer doesn't kill the pill.
        }
    }));
}

// ── Core render ───────────────────────────────────────────────────────────────

function drawMap(canvas: HTMLCanvasElement, payload: MapPayload): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bg = payload.background;

    // ── Compute canvas size (D-02) ────────────────────────────────────────────
    // Known farm_type (FarmTypeMap hit) → sprite-driven dims at integer 2× scale.
    // Unknown farm_type → existing Phase 86 bbox math with MIN_TILES floor.
    let canvasW: number;
    let canvasH: number;

    if (bg.pixel_w !== null && bg.pixel_h !== null) {
        canvasW = bg.pixel_w * 2;
        canvasH = bg.pixel_h * 2;
    } else {
        const allX = payload.terrain.map(t => t.x)
            .concat(payload.buildings.map(b => b.x + b.w));
        const allY = payload.terrain.map(t => t.y)
            .concat(payload.buildings.map(b => b.y + b.h));

        let maxX = allX.length > 0 ? Math.max(MIN_TILES_X, ...allX) : MIN_TILES_X;
        let maxY = allY.length > 0 ? Math.max(MIN_TILES_Y, ...allY) : MIN_TILES_Y;

        // Defensive cap: warn and clamp if payload exceeds 100×100 tiles (future schema drift guard).
        if (maxX > 100 || maxY > 100) {
            console.warn(`stardew-map: payload bbox ${maxX}×${maxY} exceeds 100×100 — clamping to 100×100`);
            maxX = Math.min(maxX, 100);
            maxY = Math.min(maxY, 100);
        }

        canvasW = maxX * TILE_PX;
        canvasH = maxY * TILE_PX;
    }

    // Assigning canvas.width / canvas.height RESETS context state per HTML Living Standard
    // (clears bitmap, resets imageSmoothingEnabled, fillStyle, font, etc).
    canvas.width  = canvasW;
    canvas.height = canvasH;

    // Phase 88 FIX (research Pitfall 1/5): set imageSmoothingEnabled AFTER size assignment.
    // Previously this was set before — latent bug masked by the fact that the renderer only
    // drew rectangles. Phase 88 introduces drawImage which would smooth without this ordering.
    ctx.imageSmoothingEnabled = false;
    ctx.font         = '12px sans-serif';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';

    // Z-order per D-09: background → flooring → terrain (HoeDirt) → tall_features
    // (trees+fruit_trees+bushes, server-merged tile_y ASC per B1) → buildings → labels.

    // ── Base layer (D-08 #1): sprite OR hex (mutually exclusive) ──────────────
    const cached = bg.url !== null ? imgCache.get(bg.url) : undefined;
    if (cached && cached.complete && cached.naturalWidth > 0) {
        ctx.drawImage(cached, 0, 0, canvas.width, canvas.height);
    } else {
        ctx.fillStyle = bg.fallback_hex;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // ── Phase 90 Flooring layer (D-09 #1 — between background and terrain) ────
    // Per D-06: one PNG per whichFloor id, drawn tile-aligned at 1×1 (no overhang —
    // flooring is flat fill). On missing-sprite → translucent grey FLOORING_FALLBACK
    // so the path is still visible. Drawn BELOW HoeDirt per ROADMAP SC #5 so tilling
    // over flooring visually wins (matches in-game SDV render).
    for (const f of payload.flooring) {
        const fx = f.x * TILE_PX;
        const fy = f.y * TILE_PX;
        const fSprite = f.sprite_url ? imgCache.get(f.sprite_url) : null;
        if (fSprite && fSprite.complete && fSprite.naturalWidth > 0) {
            ctx.drawImage(fSprite, fx, fy, TILE_PX, TILE_PX);
        } else {
            ctx.fillStyle = FLOORING_FALLBACK;
            ctx.fillRect(fx, fy, TILE_PX, TILE_PX);
        }
    }

    // ── Terrain pass (D-08 #2 — MUST come before buildings, MAP-02 SC#2 layer order) ──
    // Path2D batching: one Path2D per color → 5 draw calls instead of 5,200 (Pitfall 3).
    // Phase 91 (D-01b): HoeDirt split by `watered` flag — dry uses TILLED_DRY_COLOR
    // (Phase 86 baseline `#7a5230`), wet uses TILLED_WATERED_COLOR (`#5e3e1e`). All
    // non-HoeDirt terrain falls through to the existing TERRAIN_COLORS lookup.
    const terrainPaths = new Map<string, Path2D>();
    for (const row of payload.terrain) {
        let color: string;
        if (row.id === 'tilled') {
            color = row.watered === true ? TILLED_WATERED_COLOR : TILLED_DRY_COLOR;
        } else {
            color = TERRAIN_COLORS[row.id] ?? TERRAIN_FALLBACK;
        }
        if (!terrainPaths.has(color)) terrainPaths.set(color, new Path2D());
        terrainPaths.get(color)!.rect(row.x * TILE_PX, row.y * TILE_PX, TILE_PX, TILE_PX);
    }
    for (const [color, path] of terrainPaths) {
        ctx.fillStyle = color;
        ctx.fill(path);
    }

    // ── Phase 91 Crops layer (D-09 — between terrain and tall_features) ───────
    // Iterate payload.crops in order; each row is drawn bottom-anchored over its
    // tile. Defensive null-coalesce per UI-SPEC error-state contract — if the
    // payload key is somehow missing (legacy snapshot? bad presenter?) we fall
    // back to an empty array instead of throwing.
    for (const c of payload.crops ?? []) {
        drawCropFeature(ctx, c);
    }

    // ── Phase 90 Tall-features layer (D-09 #3 / B1 — between terrain and buildings) ──
    // Cross-category Z-order honoured via server-side merged `tall_features` array
    // (Plan 07 / B1) — true vertical occlusion across trees+fruit_trees+bushes per
    // D-09. JS does ZERO sort/merge per repaint. Iterate in payload order (already
    // monotonic-ascending by tile_y across all 3 categories) and dispatch by the
    // `category` discriminator to one of three inline draw routines. Each routine
    // applies bottom-aligned anchoring per Phase 89 D-05:
    //   dy = (item.y + h) * TILE_PX - sprite_h * 2
    // For trees/fruit_trees h=1; for bushes h=item.h (from payload).
    for (const entry of payload.tall_features) {
        switch (entry.category) {
            case 'tree':
                drawTreeFeature(ctx, entry);
                break;
            case 'fruit_tree':
                drawFruitTreeFeature(ctx, entry);
                break;
            case 'bush':
                drawBushFeature(ctx, entry);
                break;
        }
    }

    // ── Phase 92 D-09: Objects layer (between tall_features and buildings) ────
    // Iterate payload.objects in server order; each row branches by category
    // (chest / sprinkler / scarecrow / crabpot / machine / other) and big-craftable
    // vs small-object anchor inside drawObjectFeature. Defensive null-coalesce
    // matches the Phase 91 crops loop above (legacy snapshots without `objects`).
    for (const o of payload.objects ?? []) {
        drawObjectFeature(ctx, o);
    }

    // ── Buildings pass (D-08 #3 — executes AFTER terrain pass — correct layer order) ──
    // Phase 89: sprite-or-fallback branch per UI-SPEC §Per-building primary draw outcomes.
    // Font/textAlign/textBaseline already set above (post-resize). Re-set fillStyle below.
    for (const b of payload.buildings) {
        const bx = b.x * TILE_PX;
        const by = b.y * TILE_PX;
        const bw = b.w * TILE_PX;
        const bh = b.h * TILE_PX;

        const sprite = b.sprite_url ? imgCache.get(b.sprite_url) : null;
        if (sprite && sprite.complete && sprite.naturalWidth > 0 && b.sprite_w && b.sprite_h) {
            // Primary sprite path (D-05 bottom-aligned, upward overhang).
            const dx = bx;
            const dy = (b.y + b.h) * TILE_PX - b.sprite_h * 2;
            const dw = b.sprite_w * 2;
            const dh = b.sprite_h * 2;

            // Stage overlay (UI-SPEC mechanism C) — stage<2 draws the sprite at REDUCED alpha
            // (terrain shows through, building reads dimmer) + 8×8 diagonal-hatch overlay on
            // the footprint. Stage 2 (finished) draws the sprite at full opacity.
            // UAT 2026-05-28: previously this drew the sprite at full alpha unconditionally
            // BEFORE the stage<2 branch, then over-painted at reduced alpha on top — the
            // overpaint did not dim the building. Gating the full draw to stage 2 fixes it.
            if (b.stage < 2) {
                ctx.save();
                ctx.globalAlpha = b.stage === 0 ? 0.35 : 0.65;
                ctx.drawImage(sprite, dx, dy, dw, dh);
                ctx.globalAlpha = 1.0;
                if (SCAFFOLD_HATCH !== null) {
                    ctx.fillStyle = SCAFFOLD_HATCH;
                    ctx.fillRect(bx, by, bw, bh);
                }
                ctx.restore();
                ctx.globalAlpha = 1.0;
            } else {
                ctx.drawImage(sprite, dx, dy, dw, dh);
            }
            // No label drawn in sprite path (UI-SPEC layer #5 — fallback-only labels).
        } else {
            // Fallback path — Phase 86 verbatim (colored rect + centered white label).
            ctx.fillStyle = getBuildingColor(b.type);
            ctx.fillRect(bx, by, bw, bh);
            ctx.fillStyle = '#ffffff';
            ctx.fillText(b.type, bx + bw / 2, by + bh / 2, bw - 4);
        }

        // ── Phase 93 D-03b/c: animal_badge pill overlay ─────────────────────────
        // Drawn AFTER sprite/fallback so it always renders on top of the building.
        // Only rendered when b.animal_badge is present and count > 0.
        if (b.animal_badge && b.animal_badge.count > 0) {
            const text = `${b.animal_badge.emoji} ${b.animal_badge.count}`;
            const padX = 4, padY = 2;
            ctx.font = `${Math.round(TILE_PX * 0.5)}px sans-serif`;
            const metrics = ctx.measureText(text);
            const pillW = metrics.width + padX * 2;
            const pillH = TILE_PX * 0.5 + padY * 2;
            // Anchor bottom-right of building bbox, offset (-4, -4) per CONTEXT D-03c
            const px = (b.x + b.w) * TILE_PX - pillW - 4;
            const py = (b.y + b.h) * TILE_PX - pillH - 4;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
            roundRect(ctx, px, py, pillW, pillH, 4);
            ctx.fill();
            ctx.fillStyle = 'white';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            ctx.fillText(text, px + padX, py + padY);
            // Restore drawMap defaults
            ctx.textAlign    = 'center';
            ctx.textBaseline = 'middle';
        }

        // ── Phase 93.1 (Plan 14 / FARM-01..03): farmer_badge pill at building TOP-LEFT ──
        // Structural twin of the animal_badge pill above — coexists on busy FarmHouse
        // without overlap (animal pill is BOTTOM-RIGHT). Renders up to 3 indoor farmer
        // head-sprites at half-scale (16×20 from 32×40 cached), then a +N overflow text.
        const fbadge = b.farmer_badge;
        if (fbadge && fbadge.count > 0) {
            const HEAD_W = 16, HEAD_H = 20, GAP = 2, PAD = 2;
            const baseX = b.x * TILE_PX + PAD;
            const baseY = b.y * TILE_PX + PAD;
            const maxToDraw = Math.min(3, fbadge.farmer_names.length);
            const extra = fbadge.count - maxToDraw;
            // Strip backdrop (dark rounded-rect) — reserve extra width for +N overlay if needed
            const overflowSlot = extra > 0 ? HEAD_W : 0;
            const stripW = maxToDraw * HEAD_W + Math.max(0, maxToDraw - 1) * GAP + overflowSlot + PAD * 2;
            const stripH = HEAD_H + PAD * 2;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
            roundRect(ctx, baseX - PAD, baseY - PAD, stripW, stripH, 4);
            ctx.fill();
            // Per-head composite (race-safe: skip if not yet in cache; drawMap repaints
            // once bootstrapHeadCanvases finishes — same pattern as Plan 06 marker loop)
            for (let i = 0; i < maxToDraw; i++) {
                const name = fbadge.farmer_names[i];
                const off = headCanvasCache.get(name);
                if (!off) continue;
                const dx = baseX + i * (HEAD_W + GAP);
                ctx.drawImage(off, dx, baseY, HEAD_W, HEAD_H);
            }
            // +N overflow text (white over dark stroke for legibility on any backdrop)
            if (extra > 0) {
                const tx = baseX + maxToDraw * (HEAD_W + GAP);
                const ty = baseY + 2;
                ctx.font = `${Math.round(TILE_PX * 0.4)}px sans-serif`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';
                ctx.lineWidth = 2;
                ctx.strokeStyle = 'rgba(0,0,0,0.8)';
                ctx.fillStyle = 'white';
                ctx.strokeText(`+${extra}`, tx, ty);
                ctx.fillText(`+${extra}`, tx, ty);
            }
            // Restore drawMap canvas-context defaults (matches Plan 06 hygiene)
            ctx.font         = '12px sans-serif';
            ctx.textAlign    = 'center';
            ctx.textBaseline = 'middle';
            ctx.lineWidth    = 1;
        }
    }

    // ── Phase 93 D-06 layer 7: Animals layer (above buildings, below farmer markers) ──
    // Iterate payload.animals in server order (sorted tile_y ASC by StardewModel for
    // stable within-layer z-order per Phase 90/91/92 pattern). Defensive null-coalesce
    // matches the Phase 91 crops loop above (absent key → empty array, no throw).
    for (const a of payload.animals ?? []) {
        drawAnimalFeature(ctx, a);
    }

    // ── Phase 93 D-06 layer 8: Farmer markers (above animals — farmers not occluded) ──
    // Sync drawImage from headCanvasCache (bootstrapped ONCE per payload load via
    // bootstrapHeadCanvases — RESEARCH Pitfall 5). If a farmer is not yet in cache
    // (bootstrap race on very first load), skip silently — next drawMap call will
    // have them. Name label uses stroke+fill for legibility against varying backgrounds.
    for (const m of payload.farmer_markers ?? []) {
        const off = headCanvasCache.get(m.farmer_name);
        if (!off) continue;
        const dx = m.x * TILE_PX;
        const dy = (m.y + 1) * TILE_PX - off.height;
        ctx.drawImage(off, dx, dy);
        // Name label below head sprite
        ctx.fillStyle   = 'white';
        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.lineWidth   = 3;
        ctx.font        = '10px monospace';
        ctx.textAlign   = 'center';
        ctx.textBaseline = 'alphabetic';
        const labelX = dx + off.width / 2;
        const labelY = dy + off.height + 12;
        ctx.strokeText(m.farmer_name, labelX, labelY);
        ctx.fillText(m.farmer_name, labelX, labelY);
        // Restore drawMap text defaults (font was just set to 10px monospace)
        ctx.font         = '12px sans-serif';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineWidth    = 1;
    }
}

// ── Hit-testing ───────────────────────────────────────────────────────────────

function hitTestBuilding(buildings: BuildingRow[], tileX: number, tileY: number): BuildingRow | null {
    // O(N) linear scan — acceptable for ~10-30 buildings per farm (RESEARCH Don't Hand-Roll table).
    for (const b of buildings) {
        if (tileX >= b.x && tileX < b.x + b.w && tileY >= b.y && tileY < b.y + b.h) {
            return b;
        }
    }
    return null;
}

// ── Phase 90 (D-11): Terrain-feature hit-test ─────────────────────────────────
// Discriminated-union return per RESEARCH §Q10 (cleaner than 3 separate functions).
// Hit-tests iterate the PER-CATEGORY arrays (payload.trees / .fruit_trees /
// .bushes) — NOT payload.tall_features (which is the draw-order projection;
// per-category arrays are the canonical hit-test source). Flooring is excluded
// from tooltip per D-11 (passive decor, like terrain).
type TerrainHit =
    | { kind: 'tree';       row: TreeRow }
    | { kind: 'fruit_tree'; row: FruitTreeRow }
    | { kind: 'bush';       row: BushRow };

function hitTestTerrainFeature(payload: MapPayload, tileX: number, tileY: number): TerrainHit | null {
    // Trees: 1×1 tile-bbox match.
    for (const t of payload.trees) {
        if (tileX === t.x && tileY === t.y) {
            return { kind: 'tree', row: t };
        }
    }
    // Fruit trees: 1×1 tile-bbox match.
    for (const f of payload.fruit_trees) {
        if (tileX === f.x && tileY === f.y) {
            return { kind: 'fruit_tree', row: f };
        }
    }
    // Bushes: w×h tile-bbox match (per Phase 89 D-06 contract).
    for (const b of payload.bushes) {
        if (tileX >= b.x && tileX < b.x + b.w && tileY >= b.y && tileY < b.y + b.h) {
            return { kind: 'bush', row: b };
        }
    }
    return null;
}

// Phase 90 (D-11): Tooltip text formatter per the CONTEXT.md D-11 table.
//   * Tree (stage === 5)  → "<DisplayName> stump"
//   * Tree (stage < 5)    → "<DisplayName> (stage <n>)"
//   * FruitTree (stage === 4 && fruit_count > 0)
//                         → "<DisplayName> (stage <n>, <count> fruit)"
//   * FruitTree (else)    → "<DisplayName> (stage <n>)"
//   * Bush                → "<DisplayName>"
function formatTerrainTooltip(hit: TerrainHit): string {
    switch (hit.kind) {
        case 'tree': {
            const t = hit.row;
            if (t.stage === 5) {
                return `${t.display_name} stump`;
            }
            return `${t.display_name} (stage ${t.stage})`;
        }
        case 'fruit_tree': {
            const f = hit.row;
            if (f.stage === 4 && f.fruit_count > 0) {
                return `${f.display_name} (stage ${f.stage}, ${f.fruit_count} fruit)`;
            }
            return `${f.display_name} (stage ${f.stage})`;
        }
        case 'bush':
            return hit.row.display_name;
    }
}

// ── Phase 91 (D-11): Crop hit-test ────────────────────────────────────────────
// 1x1 tile-bbox match per Phase 89 D-06 contract. Iterates payload.crops with
// defensive null-coalesce so a missing key returns null (no tooltip) rather
// than throwing.
function hitTestCrop(payload: MapPayload, tileX: number, tileY: number): { kind: 'crop'; row: CropRow } | null {
    for (const c of payload.crops ?? []) {
        if (tileX === c.x && tileY === c.y) {
            return { kind: 'crop', row: c };
        }
    }
    return null;
}

// ── Phase 92 (D-11): Object hit-test ─────────────────────────────────────────
// 1×1 tile-bbox match per Phase 89 D-06 contract. Mirrors hitTestCrop shape.
function hitTestObject(payload: MapPayload, tileX: number, tileY: number): { kind: 'object'; row: ObjectRow } | null {
    for (const o of payload.objects ?? []) {
        if (tileX === o.x && tileY === o.y) {
            return { kind: 'object', row: o };
        }
    }
    return null;
}

// ── Phase 93 (ANIM-03 / D-08): Species emoji helper ──────────────────────────
// Maps animal_type strings to emoji for building tooltip species histogram (D-08).
// Uses substring includes() to handle baby variants (e.g. 'Baby White Chicken' → 🐔).
// Fallback '🐾' for any uncovered species.
function emojiForSpecies(species: string): string {
    if (species.includes('Cow'))       return '🐄';
    if (species.includes('Chicken'))   return '🐔';
    if (species.includes('Duck'))      return '🐤';
    if (species.includes('Goat'))      return '🐐';
    if (species.includes('Sheep'))     return '🐑';
    if (species.includes('Pig'))       return '🐖';
    if (species.includes('Rabbit'))    return '🐰';
    if (species.includes('Ostrich'))   return '🐔';
    if (species.includes('Dinosaur'))  return '🦖';
    return '🐾';
}

// ── Phase 93 (ANIM-03 / D-08): Animal + Farmer Marker hit-tests ──────────────
// Both use the same 1×1 tile-bbox match as hitTestObject / hitTestCrop per Phase 89 D-06
// contract. Defensive null-coalesce on the payload arrays — old saves without Phase 93
// data return null (no tooltip) rather than throwing.

function hitTestFarmer(payload: MapPayload, tileX: number, tileY: number):
    { kind: 'farmer'; row: FarmerMarker } | null
{
    for (const m of payload.farmer_markers ?? []) {
        if (tileX === m.x && tileY === m.y) return { kind: 'farmer', row: m };
    }
    return null;
}

function hitTestAnimal(payload: MapPayload, tileX: number, tileY: number):
    { kind: 'animal'; row: AnimalRow } | null
{
    for (const a of payload.animals ?? []) {
        if (tileX === a.x && tileY === a.y) return { kind: 'animal', row: a };
    }
    return null;
}

// ── Phase 92 (D-11): SDV in-game minutes formatter ───────────────────────────
// 92-REVIEW WR-04: return a discriminated union instead of overloading a
// magic 'ready' string. m<=0 → {kind:'ready'}; otherwise {kind:'minutes',
// text:'Xh Ym'|'Nm'|'Nh'}. The caller inspects `kind` explicitly so a
// machine row with held_item set and minutes < 0 can never produce
// "ready in ready" tooltip text.
type FormattedMinutes =
    | { kind: 'ready' }
    | { kind: 'minutes'; text: string };

function formatMinutes(m: number): FormattedMinutes {
    if (m <= 0) return { kind: 'ready' };
    const h = Math.floor(m / 60);
    const mm = m % 60;
    let text: string;
    if (h === 0) text = `${mm}m`;
    else if (mm === 0) text = `${h}h`;
    else text = `${h}h ${mm}m`;
    return { kind: 'minutes', text };
}

// ── Phase 92 (D-11): Object tooltip formatter ────────────────────────────────
// Category switch per CONTEXT D-11 — state-aware copy + time formatting.
function formatObjectTooltip(hit: { kind: 'object'; row: ObjectRow }): string {
    const o = hit.row;
    const s = o.state ?? {};
    switch (o.category) {
        case 'chest': {
            const n = s.item_count ?? 0;
            return n > 0 ? `${o.display_name} — ${n} items` : o.display_name;
        }
        case 'sprinkler': {
            const tier = (s.tier ?? 1) as 1 | 2 | 3;
            const baseTiles = tier === 1 ? 4 : (tier === 2 ? 8 : 24);
            const extra = s.has_pressure_nozzle ? ' + Pressure Nozzle' : '';
            const ench  = s.has_enricher ? ' + Enricher' : '';
            return `${o.display_name} — waters ${baseTiles} tiles${extra}${ench}`;
        }
        case 'scarecrow':
            return `${o.display_name} — protects ${s.radius ?? 8} tiles`;
        case 'crabpot':
            if (s.held_item_name)  return `Crab Pot — ${s.held_item_name}`;
            if (s.bait_name)       return `Crab Pot — bait: ${s.bait_name}`;
            return 'Crab Pot — empty';
        case 'machine': {
            if (s.ready)           return `${o.display_name} — ${s.held_item_name ?? 'item'} ready`;
            if (s.held_item_name) {
                // 92-REVIEW WR-04: inspect the formatter's discriminator explicitly
                // so a machine row with held_item set and minutes <= 0 (data drift or
                // mid-tick save) reads as "<name> ready" instead of "ready in ready".
                const f = formatMinutes(s.minutes_until_ready ?? 0);
                return f.kind === 'ready'
                    ? `${o.display_name} — ${s.held_item_name} ready`
                    : `${o.display_name} — making ${s.held_item_name}, ready in ${f.text}`;
            }
            return `${o.display_name} — empty`;
        }
        default:
            return o.display_name;
    }
}

// ── Phase 92 (D-03c / D-09): Hover-only radius overlay ───────────────────────
// Painted ABOVE buildings (post-layer-6) so it's never clipped by a barn footprint.
// Sprinkler: tile-set from SPRINKLER_TILES const + Pressure Nozzle one-ring expansion.
// Scarecrow: circular ring at radius 8 (regular/rarecrow) or 16 (Deluxe) per state.radius.
function drawHoverOverlay(ctx: CanvasRenderingContext2D, o: ObjectRow): void {
    if (o.category === 'sprinkler') {
        const tier = (o.state?.tier ?? 1) as 1 | 2 | 3;
        const tiles = [...SPRINKLER_TILES[tier]];
        // Pressure Nozzle: one-tier upgrade per SDV mechanic (RESEARCH §Pattern 3).
        // tier 1+PN ≈ tier 2, tier 2+PN ≈ tier 3. v1 approximation — uses next-tier set.
        if (o.state?.has_pressure_nozzle) {
            if (tier === 1) tiles.push(...SPRINKLER_TILES[2].filter(([x, y]) => !tiles.some(([tx, ty]) => tx === x && ty === y)));
            else if (tier === 2) tiles.push(...SPRINKLER_TILES[3].filter(([x, y]) => !tiles.some(([tx, ty]) => tx === x && ty === y)));
        }
        ctx.fillStyle   = 'rgba(70,130,180,0.15)';
        ctx.strokeStyle = 'rgba(70,130,180,0.50)';
        ctx.lineWidth   = 1;
        for (const [dx, dy] of tiles) {
            const tx = (o.x + dx) * TILE_PX;
            const ty = (o.y + dy) * TILE_PX;
            ctx.fillRect(tx, ty, TILE_PX, TILE_PX);
            ctx.strokeRect(tx, ty, TILE_PX, TILE_PX);
        }
    } else if (o.category === 'scarecrow') {
        const r = o.state?.radius ?? 8;
        const cx = (o.x + 0.5) * TILE_PX;
        const cy = (o.y + 0.5) * TILE_PX;
        ctx.fillStyle   = 'rgba(180,120,70,0.12)';
        ctx.strokeStyle = 'rgba(180,120,70,0.45)';
        ctx.lineWidth   = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, r * TILE_PX, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    }
}

// ── Tooltip wiring ────────────────────────────────────────────────────────────
// Phase 93 (D-08): Final chain order (top-most wins):
//   1. hitTestFarmer  (NEW — farmer markers drawn above all layers, top priority)
//   2. hitTestAnimal  (NEW — animals rendered above buildings, below farmers)
//   3. hitTestObject  (Phase 92)
//   4. hitTestBuilding (Phase 89, extended with species histogram tooltip)
//   5. hitTestTerrainFeature (Phase 90)
//   6. hitTestCrop    (Phase 91)
//   7. Flooring — no tooltip (Phase 90 D-11)

function wireTooltip(canvas: HTMLCanvasElement, tooltip: HTMLElement, payload: MapPayload): void {
    // 92-REVIEW WR-01: cache the last hovered tile to suppress redundant work.
    // Mousemove fires at 60-1000 Hz; if the cursor jitters inside a single
    // tile we used to run drawMap + drawHoverOverlay on every event, which is
    // O(everything) (terrain Path2D batch, flooring, crops, tall_features,
    // objects, buildings, labels). Now hit-tests + tooltip reposition still
    // run every event, but drawMap + drawHoverOverlay only fire when the
    // (tileX, tileY) coordinate actually changes. Side-benefit: the radius
    // ring no longer flickers as the cursor jitters within the source tile.
    let lastHoverTile: { x: number; y: number } | null = null;

    canvas.addEventListener('pointermove', (e: PointerEvent) => {
        if (e.pointerType !== 'mouse') return;   // D-04: tooltip + hover-overlay drawMap are mouse-only (SC#4)
        // Convert CSS-pixel event coords → canvas backbuffer coords → tile coords.
        // getBoundingClientRect is required — Pitfall 6: "tooltip works at 100% zoom only" without it.
        // Resize-safe (research Pitfall 4): canvas.width and rect.width are read live each pointermove,
        // so the Phase 88 sprite-onload repaint that may resize the canvas does not break hit-testing.
        const rect   = canvas.getBoundingClientRect();
        const cssX   = e.clientX - rect.left;
        const cssY   = e.clientY - rect.top;
        const canvasX = cssX * (canvas.width  / rect.width);
        const canvasY = cssY * (canvas.height / rect.height);
        const tileX  = Math.floor(canvasX / TILE_PX);
        const tileY  = Math.floor(canvasY / TILE_PX);

        const tileChanged = lastHoverTile === null
            || lastHoverTile.x !== tileX
            || lastHoverTile.y !== tileY;

        // Phase 93 (D-08): Farmer marker hit-test — TOP of chain (topmost wins).
        // Farmer markers are drawn above all other layers (D-06), so they should
        // take tooltip priority. current_location is always 'Farm' (filtered server-side per D-04d).
        const farmerHit = hitTestFarmer(payload, tileX, tileY);
        if (farmerHit) {
            tooltip.textContent = farmerHit.row.farmer_name;
            tooltip.style.left  = (e.clientX + TOOLTIP_OFFSET_X) + 'px';
            tooltip.style.top   = (e.clientY + TOOLTIP_OFFSET_Y) + 'px';
            tooltip.hidden      = false;
            lastHoverTile = { x: tileX, y: tileY };
            return;
        }

        // Phase 93 (D-08): Animal hit-test — second in chain (after farmer, before object).
        // Animals rendered above buildings but below farmer markers (D-06).
        const animalHit = hitTestAnimal(payload, tileX, tileY);
        if (animalHit) {
            const r = animalHit.row;
            tooltip.textContent = `${r.animal_name} (${r.animal_type}) — ${r.state.hearts} ♥, ${r.state.mood}`;
            tooltip.style.left  = (e.clientX + TOOLTIP_OFFSET_X) + 'px';
            tooltip.style.top   = (e.clientY + TOOLTIP_OFFSET_Y) + 'px';
            tooltip.hidden      = false;
            lastHoverTile = { x: tileX, y: tileY };
            return;
        }

        // Phase 90 (D-11): Tooltip chain — building hit-test after farmer + animal (D-08 final order).
        // Flooring has NO tooltip per D-11.
        const hit = hitTestBuilding(payload.buildings, tileX, tileY);
        if (hit) {
            // Phase 89 — UI-SPEC §Copywriting Contract: surface construction state in tooltip copy.
            let buildingTooltip = hit.stage < 2 && hit.days_left > 0
                ? `${hit.type} — under construction (${hit.days_left} days left)`
                : hit.type;
            // Phase 93 (D-08): Append species histogram when building has indoor animals.
            // Format: "<building_name> — <emoji> <count> <species>, <emoji> <count> <species>"
            // Sorted by count DESC. Separator is ' — ' (textContent does not render \n).
            if (hit.animal_species_histogram) {
                const entries = Object.entries(hit.animal_species_histogram)
                    .sort((a, b) => b[1] - a[1]);
                if (entries.length > 0) {
                    const parts = entries.map(([species, count]) =>
                        `${emojiForSpecies(species)} ${count} ${species}`);
                    buildingTooltip += ' — ' + parts.join(', ');
                }
            }
            // Phase 93.1 (Plan 15 / FARM-01,FARM-03) gap closure: surface indoor farmers
            // on building hover (mirrors species histogram). Separator ' — ' matches
            // Plan 07's choice (textContent does not render \n).
            const fbadge = hit.farmer_badge;
            if (fbadge && fbadge.count > 0 && Array.isArray(fbadge.farmer_names) && fbadge.farmer_names.length > 0) {
                buildingTooltip += ' — Farmers here: ' + fbadge.farmer_names.join(', ');
            }
            tooltip.textContent = buildingTooltip;
            tooltip.style.left  = (e.clientX + TOOLTIP_OFFSET_X) + 'px';
            tooltip.style.top   = (e.clientY + TOOLTIP_OFFSET_Y) + 'px';
            tooltip.hidden      = false;
            return;
        }

        const terrainHit = hitTestTerrainFeature(payload, tileX, tileY);
        if (terrainHit) {
            tooltip.textContent = formatTerrainTooltip(terrainHit);
            tooltip.style.left  = (e.clientX + TOOLTIP_OFFSET_X) + 'px';
            tooltip.style.top   = (e.clientY + TOOLTIP_OFFSET_Y) + 'px';
            tooltip.hidden      = false;
            return;
        }

        // Phase 91 (D-11): Crop hit-test AFTER terrain, BEFORE flooring no-op.
        // Tooltip copy per UI-SPEC Copywriting Contract — 1-indexed phase for
        // human readability (XML stores 0-indexed currentPhase). When sprite_url
        // is null (CropSpriteMap miss) display_name may be the raw harvest_id;
        // tooltip still works (no special branch needed — display_name is the
        // server-resolved label either way).
        const cropHit = hitTestCrop(payload, tileX, tileY);
        if (cropHit) {
            const c = cropHit.row;
            tooltip.textContent = c.dead
                ? `${c.display_name} (dead)`
                : `${c.display_name} (phase ${c.phase + 1} of ${c.phase_count})`;
            tooltip.style.left = (e.clientX + TOOLTIP_OFFSET_X) + 'px';
            tooltip.style.top  = (e.clientY + TOOLTIP_OFFSET_Y) + 'px';
            tooltip.hidden     = false;
            return;
        }

        // ── Phase 92 (D-11): Object hit (after crop, before no-tooltip fallthrough) ──
        // Object hit ALSO triggers drawHoverOverlay for sprinkler/scarecrow per D-03c.
        // Overlay paints ABOVE buildings via full drawMap repaint + post-pass — never
        // clipped by a barn footprint. Other categories (chest/crabpot/machine/other)
        // get the tooltip without overlay (no flicker).
        const objectHit = hitTestObject(payload, tileX, tileY);
        if (objectHit) {
            const o = objectHit.row;
            tooltip.textContent = formatObjectTooltip(objectHit);
            tooltip.style.left  = (e.clientX + TOOLTIP_OFFSET_X) + 'px';
            tooltip.style.top   = (e.clientY + TOOLTIP_OFFSET_Y) + 'px';
            tooltip.hidden      = false;
            // 92-REVIEW WR-01: only repaint the radius overlay when the hovered
            // tile actually changes. Mousemove inside the same tile no longer
            // triggers a full drawMap pass (which would also clear-and-redraw
            // the ring, causing flicker).
            if ((o.category === 'sprinkler' || o.category === 'scarecrow') && tileChanged) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    drawMap(canvas, payload);
                    drawHoverOverlay(ctx, o);
                }
            }
            lastHoverTile = { x: tileX, y: tileY };
            return;
        }

        // 92-REVIEW WR-01: leaving a sprinkler/scarecrow tile must clear the
        // overlay. drawMap repaints from scratch (no overlay). Only repaint
        // when the cursor moves OFF a radius-bearing tile, not on every
        // mousemove over empty space.
        if (lastHoverTile !== null && tileChanged) {
            const prev = hitTestObject(payload, lastHoverTile.x, lastHoverTile.y);
            if (prev && (prev.row.category === 'sprinkler' || prev.row.category === 'scarecrow')) {
                drawMap(canvas, payload);
            }
        }
        lastHoverTile = { x: tileX, y: tileY };
        tooltip.hidden = true;
    });

    canvas.addEventListener('mouseleave', () => {
        tooltip.hidden = true;
        // 92-REVIEW WR-01: leaving the canvas while hovering a sprinkler /
        // scarecrow tile would otherwise leave the radius ring painted until
        // the next render trigger. Repaint once to clear; reset hover state.
        if (lastHoverTile !== null) {
            const prev = hitTestObject(payload, lastHoverTile.x, lastHoverTile.y);
            if (prev && (prev.row.category === 'sprinkler' || prev.row.category === 'scarecrow')) {
                drawMap(canvas, payload);
            }
            lastHoverTile = null;
        }
    });
}

// ── Module default export (Option A — matches src/index.ts svFarmer.init() convention) ──

export default {
    // Phase 93: init() is now async to await bootstrapHeadCanvases() before first drawMap call.
    // Per RESEARCH Pitfall 5 — initHeadCanvas is async + expensive; bootstrap ONCE per payload load.
    // The returned Promise is not awaited by index.ts (fire-and-forget); the first drawMap() call
    // at the end of bootstrap renders synchronously; prior synchronous drawMap() below renders
    // the map immediately with fallback markers, then re-renders after bootstrap completes.
    async init(): Promise<void> {
        // Page-marker guard: no-op on every page that does NOT have a [data-farm-map] canvas.
        const canvas = document.querySelector<HTMLCanvasElement>('[data-farm-map]');
        if (!canvas) return;

        const raw     = canvas.dataset.farmMap;
        const tooltip = document.querySelector<HTMLElement>('.sv-map-tooltip');
        if (!raw || !tooltip) return;

        // 89-REVIEW WR-02: narrow try/catch to JSON.parse only. Previously this
        // wrapped the entire init body (drawMap, wireTooltip, aria-label rewrite,
        // async fetch loop) and silently swallowed every error from any of them,
        // which would mask real bugs as "the map just doesn't appear." Now a
        // malformed payload short-circuits cleanly while runtime errors in the
        // render / wiring code propagate to the console for diagnosis.
        let payload: MapPayload;
        try {
            payload = JSON.parse(raw) as MapPayload;
        } catch {
            // Skip silently on parse error — matches stardew-avatar.ts convention.
            // UI-SPEC error-state copy ("Farm map could not be drawn. Please refresh.") handles user-facing fallback.
            return;
        }

        // Phase 93 (FARM-01 / RESEARCH Pitfall 5): bootstrap head canvases BEFORE first drawMap.
        // This ensures farmer markers are in headCanvasCache when drawMap runs.
        // bootstrapHeadCanvases is awaited once; subsequent drawMap calls (from sprite onload)
        // are sync drawImage from the cached canvases — no flicker per Pitfall 5.
        await bootstrapHeadCanvases(payload);

        drawMap(canvas, payload);
        wireTooltip(canvas, tooltip, payload);

        // Phase 131 — Panzoom init-once (MAP-01: pinch-zoom + two-finger pan).
        // Guard: Anti-Pattern 3 — NEVER init inside drawMap (re-runs per img.onload).
        // init() already early-returns when canvas is absent; this dataset guard is belt-and-suspenders.
        if (canvas.dataset.panzoomInit !== '1') {
            canvas.dataset.panzoomInit = '1';
            const wrap = canvas.parentElement as HTMLElement;  // .sv-farm-map-wrap (gesture viewport, D-05)

            // maxScale formula: at least 5× but always enough to reach 1 backbuffer px per CSS px.
            // Math.max(5, fitScale) — Pitfall 6 / D-02 calibration; ~7× Frontier, ~15× IF2R on 375px phone.
            // WR-03: guard a zero-width rect (display:none ancestor / detached) so fitScale can't be
            // Infinity; clamp the ceiling to a finite 20× so maxScale is always a sane finite number.
            const rectW    = canvas.getBoundingClientRect().width;
            const fitScale = rectW > 0 ? canvas.width / rectW : 5;

            const pz = Panzoom(canvas, {
                noBind: true,             // manual bind below filters mouse (D-01a / Research F-1)
                canvas: true,             // IN-01: load-bearing — redirects Panzoom's cursor write to the
                                          // parent (wrap) so the SCSS .sv-farm-map-canvas crosshair survives,
                                          // and aligns the manual down-bind target (wrap) with library canvas-mode
                minScale: 1,              // D-02: fitted overview is the zoom floor
                maxScale: Math.min(20, Math.max(5, fitScale)),  // D-02 ceiling; spike-tunable per device UAT
                pinchAndPan: true,        // REQUIRED for MAP-01 two-finger pan (Research F-3; 4.5.0+)
                panOnlyWhenZoomed: true,  // D-01 escape hatch, shipped ON: rest state is undraggable (Pitfall 7)
                cursor: '',               // preserve SCSS crosshair contract (Pitfall 4)
                // defaults doing the right thing (do not set): touchAction: 'none' (inline on canvas AND wrap),
                // overflow: 'hidden' (inline on wrap), startScale: 1; zoomWithWheel NOT set (D-01a)
            });

            const isNotMouse = (e: PointerEvent) => e.pointerType !== 'mouse';
            wrap.addEventListener('pointerdown', (e) => { if (isNotMouse(e)) pz.handleDown(e); });
            document.addEventListener('pointermove', (e) => { if (isNotMouse(e)) pz.handleMove(e); }, { passive: true });
            document.addEventListener('pointerup',   (e) => { if (isNotMouse(e)) pz.handleUp(e);   }, { passive: true });
            // Mirrors Panzoom's own bind(): down on parent (canvas:true semantics), move/up on document, passive.

            // Snap-home on scale-reset (Pitfall 7) — no zoom chrome required.
            // WR-01: { force: true } is load-bearing. Plain pz.pan(0,0) routes through constrainXY,
            // which returns {x,y} UNCHANGED when panOnlyWhenZoomed && scale === startScale (=1) — i.e.
            // exactly the zoom floor this handler targets — so the re-center was a silent no-op.
            // force bypasses the panOnlyWhenZoomed guard so the map actually recenters at scale 1.
            canvas.addEventListener('panzoomend', () => {
                if (pz.getScale() <= 1.001) pz.pan(0, 0, { force: true });
            });
        }

        // Phase 89 — UI-SPEC §Accessibility: extend aria-label with sprite + under-construction counts.
        // Latte server-side aria-label provides a baseline; this overwrites client-side with the richer copy.
        const totalBuildings = payload.buildings.length;
        const underConstruction = payload.buildings.filter(b => b.stage < 2).length;
        const baseLabel = canvas.getAttribute('aria-label') || '';
        // Preserve the leading "Top-down map of ... farm:" prefix from the server render by replacing
        // only the trailing buildings clause; if the existing label doesn't match the expected shape,
        // fall back to writing a complete sentence.
        const prefixMatch = baseLabel.match(/^(.*farm):/);
        const prefix = prefixMatch ? prefixMatch[1] : 'Farm map';
        canvas.setAttribute(
            'aria-label',
            `${prefix}: ${totalBuildings} buildings rendered as sprites, including ${underConstruction} under construction`
        );

        // Phase 88 — kick off async sprite fetch if not already cached + ready.
        // Per D-01 hex-first-swap-on-load contract:
        //   - First drawMap above paints hex base + terrain + buildings synchronously.
        //   - On img.onload we call drawMap again (D-06 centralized layer order via full repaint).
        //   - On img.onerror we no-op silently (D-03 — hex layer already painted, no console.warn).
        const bgUrl = payload.background.url;
        if (bgUrl !== null) {
            const existing = imgCache.get(bgUrl);
            if (!existing || !existing.complete || existing.naturalWidth === 0) {
                const img = new Image();
                // crossOrigin MUST be set BEFORE src= (MDN HTMLImageElement.crossOrigin)
                img.crossOrigin = 'anonymous';
                // Insert into cache IMMEDIATELY at fetch initiation (NOT at onload) —
                // research Pitfall 2 dedup against rapid double-fetch.
                imgCache.set(bgUrl, img);
                img.onload = () => drawMap(canvas, payload);
                img.onerror = () => { /* silent — D-03; hex layer already painted */ };
                img.src = bgUrl;
            }
        }

        // Phase 89 — kick off async per-building sprite fetches. Mirrors the Phase 88 background
        // pattern: insert into shared imgCache at fetch initiation (NOT at onload) to dedupe rapid
        // re-fetch; onload triggers full drawMap repaint (D-06 centralized layer order); onerror
        // logs once-per-URL via module-scope warnedSet (D-07 / RESEARCH Pitfall 9).
        for (const b of payload.buildings) {
            if (!b.sprite_url) continue;
            if (imgCache.has(b.sprite_url)) continue;  // already loaded or in-flight
            const url = b.sprite_url;  // narrow to non-null for closure capture
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`stardew-map: building sprite failed to load: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }

        // Phase 90 — kick off async sprite fetches for the 4 new terrain-feature
        // categories. Iterate the PER-CATEGORY arrays (NOT payload.tall_features —
        // tall_features is the draw-order projection; per-category arrays are the
        // canonical URL source and avoid double-enqueue). Identical kick-off
        // template to the buildings loop above:
        //   * crossOrigin = 'anonymous' BEFORE src= (MDN rule)
        //   * imgCache.set BEFORE src= (Pitfall 2 / Phase 88 — dedupe rapid double-fetch)
        //   * onload → full repaint via drawMap (D-06 centralised layer order)
        //   * onerror → per-URL warn dedupe via module-scope warnedSet
        for (const f of payload.flooring) {
            if (!f.sprite_url) continue;
            if (imgCache.has(f.sprite_url)) continue;
            const url = f.sprite_url;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`stardew-map: flooring sprite failed to load: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }
        for (const t of payload.trees) {
            if (!t.sprite_url) continue;
            if (imgCache.has(t.sprite_url)) continue;
            const url = t.sprite_url;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`stardew-map: tree sprite failed to load: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }
        for (const ft of payload.fruit_trees) {
            if (!ft.sprite_url) continue;
            if (imgCache.has(ft.sprite_url)) continue;
            const url = ft.sprite_url;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`stardew-map: fruit tree sprite failed to load: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }
        for (const bh of payload.bushes) {
            if (!bh.sprite_url) continue;
            if (imgCache.has(bh.sprite_url)) continue;
            const url = bh.sprite_url;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`stardew-map: bush sprite failed to load: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }

        // Phase 91 — kick off async per-crop sprite fetches. Mirrors the Phase 90
        // template above: shared imgCache + warnedSet dedupe (extended, NOT
        // forked, per 91-PATTERNS.md Pattern D); insert into cache BEFORE src=
        // (Pitfall 2 — dedupe rapid double-fetch); onload → full repaint via
        // drawMap; onerror → per-URL warn dedupe. Skip rows with sprite_url ===
        // null (CropSpriteMap miss server-side — drawCropFeature falls through
        // to D-10 shape).
        for (const c of payload.crops ?? []) {
            if (!c.sprite_url) continue;
            if (imgCache.has(c.sprite_url)) continue;
            const url = c.sprite_url;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`[stardew-map] crop sprite missing: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }

        // Phase 92 — kick off async per-object sprite fetches. Mirrors the Phase 91
        // crop fetch template above: shared imgCache + warnedSet dedupe (Pattern D —
        // extend, do NOT fork); insert into cache BEFORE src= (Pitfall 2 — dedupe
        // rapid double-fetch); onload → full drawMap repaint (D-06 centralised layer
        // order). Skip rows with sprite_url === null (SpriteMap miss server-side —
        // drawObjectFeature falls through to D-10 per-category fallback).
        for (const o of payload.objects ?? []) {
            if (!o.sprite_url) continue;
            if (imgCache.has(o.sprite_url)) continue;
            const url = o.sprite_url;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`[stardew-map] object sprite missing: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }

        // Phase 93 — kick off async per-animal sprite fetches. Extends shared imgCache
        // + warnedSet (Pattern D — extend, do NOT fork; RESEARCH Pitfall 4). Insert into
        // cache BEFORE src= (Pitfall 2 — dedupe rapid double-fetch); onload → full drawMap
        // repaint (D-06 centralised layer order). Skip rows with empty sprite_url.
        for (const a of payload.animals ?? []) {
            if (!a.sprite_url) continue;
            if (imgCache.has(a.sprite_url)) continue;
            const url = a.sprite_url;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            imgCache.set(url, img);
            img.onload  = () => drawMap(canvas, payload);
            img.onerror = () => {
                if (!warnedSet.has(url)) {
                    console.warn(`[stardew-map] animal sprite missing: ${url}`);
                    warnedSet.add(url);
                }
            };
            img.src = url;
        }
    },
};
